import { Component, inject } from '@angular/core';
import { BaseConfigurableComponent } from '@app/base-config.component';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { QuestionBase } from '@app/models/question-base';
import { TextboxQuestion } from '@app/models/questions/question-textbox';
import { QuestionControlService } from '@app/services/question/question-control.service';
import { QuestionComponent } from "../../components/shared/form/question/question.component";
import { InputType } from '@app/enum/input-type';
import { CheckboxQuestion } from '@app/models/questions/question-checkbox';
import { TextAreaQuestion } from '@app/models/questions/question-textarea';
import { PassWordQuestion } from '@app/models/questions/question-password';
import { BannerComponent } from '@app/components/shared/banner/banner.component';
import { SchedulerStepService } from '@app/services/step/scheduler-step.service';
import { Router } from '@angular/router';
import { Completion } from '@app/services/fetch/completion';
import { CategoriesService } from '@app/services/categories/categories.service';
import { Settings } from '@app/services/fetch/settings';

@Component({
  selector: 'afcu-contact-info',
  standalone: true,
  providers: [QuestionControlService],
  imports: [ReactiveFormsModule, QuestionComponent, BannerComponent],
  templateUrl: './contact-info.component.html'
})
export class ContactInfoComponent extends BaseConfigurableComponent{

  private readonly qcs = inject(QuestionControlService);
  private schedulerService = inject( SchedulerStepService );
  private categoriesService = inject( CategoriesService)
  private settingsService = inject( Settings)
  protected readonly completion = inject(Completion)

  protected router = inject( Router );

  inputType : typeof InputType = InputType
  questions: QuestionBase<string>[] = [this.FormFullName, this.FormEmail, this.FormPhoneNumber, this.FormAppointment, this.FormMemberShip, this.FormAdditionDetail].sort((a, b) => a.order - b.order)

  form: FormGroup = this.qcs.toFormGroup(this.questions as QuestionBase<string>[]);

  isSubmitted: boolean = false

  isError: boolean = false

  constructor () {
    super('page')
    this.getOrigin()
    this.completion.origin.set(this.origin)
    this.settingsService.categorySetting.set("NOTIFICATIONS")

    if(this.schedulerService.payload()['services'] === undefined)
      if(this.route.snapshot.queryParamMap.get('origin') === 'queue'){
        this.router.navigate(['splash' ], { replaceUrl: true,queryParamsHandling: 'merge', queryParams: { 'origin': 'kiosk'} })
      }else
        this.router.navigate(['services'], { replaceUrl: true })
  }

  get FormFullName() {
    return new TextboxQuestion ({
      key: 'fullName',
      label: this.getText().contactInfo.form.fullname.label,
      value: (this.schedulerService.payload()['contact-info']) ? this.schedulerService.payload()['contact-info'].fullName : '',
      placeholder: this.getText().contactInfo.form.fullname.placeholder,
      errorText: this.getText().contactInfo.form.fullname.error,
      required: true,
      order: 1
    })
  }

  get FormEmail() {
    return new TextboxQuestion ({
      key: 'email',
      label: this.getText().contactInfo.form.email.label,
      value: (this.schedulerService.payload()['contact-info']) ? this.schedulerService.payload()['contact-info'].email : '',
      placeholder: this.getText().contactInfo.form.email.placeholder,
      patternValidator: '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.(com|org|net|gov|edu|co|io|info|biz)$',
      errorText: this.getText().contactInfo.form.email.error,
      required: true,
      order: 2
    })
  }

  get FormPhoneNumber() {
    return new TextboxQuestion ({
      key: 'phone',
      label: this.getText().contactInfo.form.phone.label,
      value: (this.schedulerService.payload()['contact-info']) ? this.schedulerService.payload()['contact-info'].phone : '',
      placeholder: this.getText().contactInfo.form.phone.placeholder,
      errorText: this.getText().contactInfo.form.phone.error,
      patternValidator: '^\\d{3}-\\d{3}-\\d{4}$',
      minLength: 10,
      maxLength: 12,
      phoneMask: true,
      required: true,
      order: 3
    })
  }

  get FormAppointment() {
    return new CheckboxQuestion ({
      key: 'appointment',
      label: this.getText().contactInfo.form.appointment.label,
      value: (this.schedulerService.payload()['contact-info']) ? this.schedulerService.payload()['contact-info'].appointment : false,
      helpText: this.getText().contactInfo.form.appointment.placeholder,
      order: 4
    })
  }

  get FormMemberShip() {
    return new PassWordQuestion ({
      key: 'membershipNumber',
      label: this.getText().contactInfo.form.membership.label,
      value: (this.schedulerService.payload()['contact-info']) ? this.schedulerService.payload()['contact-info'].membershipNumber : '',
      placeholder: this.getText().contactInfo.form.membership.placeholder,
      errorText: this.getText().contactInfo.form.membership.error,
      numberOnly: true,
      minLength: 3,
      maxLength: 10,
      order: 5
    })
  }

  // h-50
  get FormAdditionDetail() {
    return new TextAreaQuestion ({
      key: 'additionDetail',
      width: 'h-50 md:h-28 lg:h-28 md:w-3/4 lg:w-3/4',
      label: this.getText().contactInfo.form.detail.label,
      value: (this.schedulerService.payload()['contact-info']) ? this.schedulerService.payload()['contact-info'].additionDetail : '',
      placeholder: this.getText().contactInfo.form.detail.placeholder,
      order: 6
    })
  }

  validateAndSetError(controlName: string): void {
    const control = this.form.get(controlName)
    const errors = control?.errors

    if (errors) {
      this.isError = true
      control?.setErrors({ 'error': true })
    }
  }

  get Settings() {
    return this.settingsService.settings()
  }

  onSubmit() {
    this.isError = false
    this.questions.forEach( question => this.validateAndSetError(question.key))

    let branchID = this.schedulerService.branchSelected().branchID

    if(branchID === undefined || branchID === null || Object.keys(branchID).length == 0){
      branchID = this.branch
      if(branchID === undefined || branchID === null) {
        this.router.navigate(['configuration'])
      }
    }

    this.isSubmitted = true
    if (this.form.valid && this.isSubmitted){
      this.schedulerService.setPayload('contact-info', this.form.getRawValue())
      const {service, staffIds} = this.categoriesService.services().serviceList.filter( service => service.service.name === this.schedulerService.payload()['services'])[0]
      const appointment:SubmissionPost= {
        serviceID: service.serviceID,
        serviceName: service.category,
        serviceDuration: service.duration,
        preBuffer: service.preBuffer,
        postBuffer: service.postBuffer,
        timezone: this.schedulerService.branchSelected().timeZone,
        branchID: branchID,
        staffIDs: (this.schedulerService.payload()['staff'] === 'all') ? staffIds : [ this.schedulerService.payload()['staff'] ],
        isSelected: (this.schedulerService.payload()['staff'] !== 'all'),
        customerName: this.form.getRawValue().fullName,
        customerEmail: this.form.getRawValue().email.toLowerCase(),
        customerPhone: this.form.getRawValue().phone.replace(/\D/g, ''),
        customerMemberNumber: this.form.getRawValue().membershipNumber,
        additionalDetails:this.form.getRawValue().additionDetail,
        smsNotifications: (this.Settings.GLOBAL.notifications_all_text_enabled) ?? (this.form.getRawValue().appointment !== '')
      }

      if (this.schedulerService.payload()['calendar'] !== undefined && this.schedulerService.payload()['calendar'] !== ''){
        appointment.scheduledStart = this.formatDate(new Date(this.schedulerService.payload()['calendar'].replace('@', '').trim()))
      }

      this.completion.submitAppointment(appointment)

    }else{
      this.isSubmitted = false
    }
  }

get errorAppointmentCompletion(): boolean {
  const error = this.completion.error();

  if (!error) return false;

  if (error.status === 409) {
    this.router.navigate(['/information/', 'notAvailable'], { queryParamsHandling: 'merge' });
    return true;
  }

  this.router.navigate(['/information/', 'submit'], { queryParamsHandling: 'merge' });
  return true; // Handle all other errors. 
}

  formatDate(parsedDate: Date):string {
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${parsedDate.getFullYear()}-${pad(parsedDate.getMonth() + 1)}-${pad(parsedDate.getDate())}T${pad(parsedDate.getHours())}:${pad(parsedDate.getMinutes())}:${pad(parsedDate.getSeconds())}`;
  }
}
